/**
 * useErrorHandler — Provides consistent error/success toast notifications across the app.
 */
import { useToast } from "@/hooks/use-toast";
import { useCallback } from "react";
import { logger } from "@/utils/logger";

type ErrorSeverity = "error" | "warning" | "info";

interface ErrorOptions {
  title?: string;
  description?: string;
  severity?: ErrorSeverity;
  duration?: number;
  silent?: boolean;
}

/**
 * Centralized error handling hook to ensure consistent error feedback
 * across the application.
 */
export const useErrorHandler = () => {
  const { toast } = useToast();

  const handleError = useCallback((error: Error | string, options: ErrorOptions = {}) => {
    const {
      title = "Error",
      description,
      severity = "error",
      duration = 5000,
      silent = false,
    } = options;

    const errorMessage = typeof error === "string" ? error : error.message;
    const finalDescription = description || errorMessage;

    if (severity === "error") {
      logger.error(`[ErrorHandler] ${title}:`, errorMessage);
    }

    if (!silent) {
      toast({
        title,
        description: finalDescription,
        variant: severity === "error" ? "destructive" : "default",
        duration,
      });
    }
  }, [toast]);

  const handleSuccess = useCallback((message: string, options: { title?: string; duration?: number } = {}) => {
    const { title = "Success", duration = 3000 } = options;
    
    toast({
      title,
      description: message,
      duration,
    });
  }, [toast]);

  return {
    handleError,
    handleSuccess,
  };
};
